"use client";

import { DismissRestoreButton } from "./DismissRestoreButton";
import { MarkdownText } from "./MarkdownText";
import type { CheckSource } from "./SourceBadge";
import { SourceBadge } from "./SourceBadge";
import { StatusBadge } from "./StatusBadge";
import {
  getImportanceColor,
  getSelectedCardClass,
  getSeverityBarColor,
} from "./style-utils";

interface CheckItemCardProps {
  id: string;
  title: string;
  detail: string;
  severity: "high" | "medium" | "low";
  status: string;
  importance: string;
  score?: number;
  rawAIScore?: number;
  scoreAdjusted?: boolean;
  source?: CheckSource;
  isSelected: boolean;
  isHighlighted?: boolean;
  isDismissed: boolean;
  onSelect: () => void;
  onDismiss: () => void;
  onRestore: () => void;
}

export function CheckItemCard({
  id,
  title,
  detail,
  severity,
  status,
  importance,
  score,
  rawAIScore,
  scoreAdjusted,
  source,
  isSelected,
  isHighlighted = false,
  isDismissed,
  onSelect,
  onDismiss,
  onRestore,
}: CheckItemCardProps) {
  return (
    <div
      id={`check-card-${id}`}
      className={`group relative flex items-start gap-4 p-4 rounded-lg border transition-all cursor-pointer w-full text-left ${getSelectedCardClass(isSelected)} ${
        isHighlighted ? "border-amber-300 ring-2 ring-amber-300 shadow-md" : ""
      }`}
    >
      <div
        className={`w-1 h-full absolute left-0 top-0 rounded-l-lg ${
          isDismissed ? "bg-zinc-300" : getSeverityBarColor(severity)
        }`}
      />
      <button
        type="button"
        className="absolute inset-0 z-0 rounded-lg focus-visible:ring-2 focus-visible:ring-blue-300"
        onClick={onSelect}
        aria-label={title}
      />
      <div className="flex-1 pl-2 relative z-10 pointer-events-none">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-bold text-sm text-gray-900 group-hover:text-blue-700 transition-colors">
            {title}
          </h4>
          {score != null && (
            <span className="text-sm font-bold tabular-nums text-gray-600">
              {score}
              {scoreAdjusted && rawAIScore != null && (
                <span className="text-xs font-normal text-amber-600 ml-1">
                  ({rawAIScore})
                </span>
              )}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-600 line-clamp-2 mb-3">
          <MarkdownText mode="inline">{detail}</MarkdownText>
        </div>
        <div className="flex items-center justify-end gap-2">
          <SourceBadge source={source} />
          <span
            className={`text-xs font-medium ${getImportanceColor(importance)}`}
          >
            {importance}
          </span>
          <StatusBadge status={status as "pass"} />
          <DismissRestoreButton
            isDismissed={isDismissed}
            onDismiss={onDismiss}
            onRestore={onRestore}
            variant="card"
          />
        </div>
      </div>
    </div>
  );
}
